import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { examAPI, categoryAPI, bookingAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import BillModal from '../../components/BillModal';
import { FiTarget, FiBarChart, FiBook, FiCheckCircle, FiClock, FiXCircle, FiFileText } from 'react-icons/fi';

const AvailableTests = () => {
  // console.log('AvailableTests Component - RENDERING');
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // console.log('AvailableTests Component - User:', user);
  // console.log('AvailableTests Component - User role:', user?.role);
  // console.log('AvailableTests Component - User ID:', user?.id);
  // console.log('AvailableTests Component - User enabled condition:', !!user);
  const queryClient = useQueryClient();
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterDifficulty, setFilterDifficulty] = useState('all');
  const [showBill, setShowBill] = useState(false);
  const [currentBill, setCurrentBill] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch user's booked tests
  const {
    data: bookingsData,
    isLoading: bookingsLoading,
    error: bookingsError
  } = useQuery({
    queryKey: ['user-bookings'],
    queryFn: () => {
      // console.log('🔍 Fetching user bookings...');
      // console.log('🔍 Current user:', user);
      // console.log('🔍 Auth token exists:', !!localStorage.getItem('token'));
      const result = bookingAPI.getMyBookings();
      // console.log('🔍 API call result:', result);
      return result;
    },
    enabled: !!user, // Only run query if user is authenticated
    refetchInterval: 60000, // Refetch every minute
  });

  // console.log('AvailableTests Component Debug:', {
  //   bookingsData,
  //   bookingsLoading,
  //   bookingsError,
  //   bookings: bookingsData?.data?.data?.bookings || []
  // });

  // Fetch categories for filter options
  const {
    data: categoriesData,
    isLoading: categoriesLoading
  } = useQuery({
    queryKey: ['exam-categories'],
    queryFn: () => {
      // console.log('🔍 Fetching exam categories...');
      return categoryAPI.getAllCategories();
    },
  });

  // console.log('Categories Debug:', {
  //   categoriesData,
  //   categoriesLoading,
  //   categories: categoriesData?.data?.data || []
  // });

  // console.log('Bookings Debug:', {
  //   bookingsData,
  //   bookingsLoading,
  //   bookingsError,
  //   bookings: bookingsData?.data?.data?.bookings || []
  // });
  
  // console.log('🔍 Full bookingsData object:', bookingsData);
  // console.log('🔍 bookingsData.data:', bookingsData?.data);
  // console.log('🔍 bookingsData.data?.bookings:', bookingsData?.data?.bookings);

  // Extract data from API responses
  const bookings = bookingsData?.data?.data?.bookings || [];
  
  // console.log('🔍 Extracted bookings:', bookings);
  // console.log('🔍 Bookings length:', bookings.length);
  // console.log('🔍 First booking:', bookings[0]);
  const categories = categoriesData?.data?.data || [];

  // Debug logging
  // console.log('AvailableTests state:', {
  //   bookingsCount: bookings.length
  // });

  // Handle bill display
  const handleShowBill = (bill) => {
    setCurrentBill(bill);
    setShowBill(true);
  };

  const handleCloseBill = () => {
    setShowBill(false);
    setCurrentBill(null);
  };

  // Filter tests based on selected criteria
  const filteredTests = bookings.filter(booking => {
    const test = booking.exam;
    if (!test) return false;
    
    const subjectMatch = filterSubject === 'all' || test.examCategory?.name === filterSubject;
    const difficultyMatch = filterDifficulty === 'all' || test.difficulty === filterDifficulty;
    
    return subjectMatch && difficultyMatch;
  });

  // Get unique subjects for filter
  const uniqueSubjects = [...new Set(bookings.map(booking => booking.exam?.examCategory?.name).filter(Boolean))];
  const uniqueDifficulties = [...new Set(bookings.map(booking => booking.exam?.difficulty).filter(Boolean))];

  if (bookingsLoading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.5rem', color: '#64748b', marginBottom: '1rem' }}>Loading your tests...</div>
        <div style={{ color: '#94a3b8' }}>Please wait while we fetch your scheduled exams.</div>
      </div>
    );
  }

  if (bookingsError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.5rem', color: '#ef4444', marginBottom: '1rem' }}>Error loading tests</div>
        <div style={{ color: '#94a3b8', marginBottom: '1rem' }}>Failed to load your scheduled exams.</div>
        <button 
          onClick={() => queryClient.invalidateQueries(['user-bookings'])}
          style={{
            backgroundColor: '#3b82f6',
            color: 'white',
            border: 'none',
            padding: '0.5rem 1rem',
            borderRadius: '0.375rem',
            cursor: 'pointer'
          }}
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-6 py-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Title Section */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                My Available Tests
              </h1>
              <p className="text-gray-600 text-lg">
                View and manage your scheduled exam bookings
              </p>
            </div>

            {/* Stats Section */}
            <div className="flex items-center gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-blue-600">{filteredTests.length}</div>
                <div className="text-sm text-gray-500">Showing</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">{bookings.length}</div>
                <div className="text-sm text-gray-500">Total Tests</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="w-full lg:w-[50%] px-6 py-6">
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: isMobile ? '6px' : '16px',
              flexWrap: isMobile ? 'nowrap' : 'wrap',
              overflowX: isMobile ? 'auto' : 'visible'
            }}
          >
            {/* Filter Controls */}
            <div className="flex-1" style={{ display: 'flex', gap: isMobile ? '6px' : '16px', flexWrap: isMobile ? 'nowrap' : 'wrap' }}>
              <div className="flex-1" style={{ minWidth: isMobile ? '120px' : '200px' }}>
                {!isMobile && (
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Filter by Subject
                  </label>
                )}
                <div className="relative">
                  <select
                    value={filterSubject}
                    onChange={(e) => setFilterSubject(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none cursor-pointer"
                    style={{ padding: isMobile ? '0.35rem 0.5rem' : undefined, height: isMobile ? '36px' : undefined, fontSize: isMobile ? '0.85rem' : undefined }}
                  >
                    <option value="all">All Subjects</option>
                    {uniqueSubjects.map((subject) => (
                      <option key={subject} value={subject}>{subject}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="flex-1" style={{ minWidth: isMobile ? '120px' : '200px' }}>
                {!isMobile && (
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Filter by Difficulty
                  </label>
                )}
                <div className="relative">
                  <select
                    value={filterDifficulty}
                    onChange={(e) => setFilterDifficulty(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none cursor-pointer"
                    style={{ padding: isMobile ? '0.35rem 0.5rem' : undefined, height: isMobile ? '36px' : undefined, fontSize: isMobile ? '0.85rem' : undefined }}
                  >
                    <option value="all">All Difficulties</option>
                    {uniqueDifficulties.map((difficulty) => (
                      <option key={difficulty} value={difficulty}>{difficulty}</option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                    <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Clear Filters Button */}
            <div className="flex gap-3" style={{ marginLeft: 'auto' }}>
              <button
                onClick={() => {
                  setFilterSubject('all');
                  setFilterDifficulty('all');
                }}
                className="px-4 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors duration-200 font-medium"
                style={{ padding: isMobile ? '0.35rem 0.6rem' : undefined, height: isMobile ? '36px' : undefined, fontSize: isMobile ? '0.85rem' : undefined }}
              >
                {isMobile ? 'Clear' : 'Clear Filters'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={`w-full ${isMobile ? 'px-0' : 'px-6'} py-8`}>

        {/* No tests message */}
        {filteredTests.length === 0 ? (
          <div className="text-center py-16 px-8 bg-gray-50 rounded-xl border border-gray-200">
            <div className="text-6xl mb-4 flex justify-center">
              <FiBook className="text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-2">
              No tests available
            </h3>
            <p className="text-gray-600">
              Contact your administrator to schedule a test for you.
            </p>
          </div>
        ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(360px, 1fr))', gap: isMobile ? '0.5rem' : '2rem' }}>
          {filteredTests.map((booking) => {
            const test = booking.exam;
            return (
              <div
                key={booking.id}
                className="dashboard-card"
                style={{ padding: isMobile ? '8px' : undefined, borderRadius: isMobile ? '12px' : undefined }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: isMobile ? '0.5rem' : '1rem' }}>
                  <div>
                    <h3 style={{ fontSize: isMobile ? '1.1rem' : '1.25rem', fontWeight: '600', color: '#1e293b', marginBottom: isMobile ? '0.25rem' : '0.5rem' }}>
                      {test.title}
                    </h3>
                    {!isMobile && (
                      <p style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem' }}>
                        {test.description || 'No description available'}
                      </p>
                    )}
                  </div>
                  <span className={`status-badge ${
                    test.difficulty === 'Advanced' ? 'status-active' :
                    test.difficulty === 'Intermediate' ? 'status-pending' : 'status-inactive'
                  }`} style={{ fontSize: isMobile ? '0.7rem' : undefined, padding: isMobile ? '2px 6px' : undefined }}>
                    {test.difficulty || 'Not specified'}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr', gap: isMobile ? '0.5rem' : '1rem', marginBottom: isMobile ? '0.75rem' : '1.5rem' }}>
                  <div>
                    <div style={{ fontSize: isMobile ? '0.8rem' : '0.875rem', color: '#64748b' }}>Subject</div>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: isMobile ? '0.95rem' : undefined }}>{test.examCategory?.name || 'Unknown'}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: isMobile ? '0.8rem' : '0.875rem', color: '#64748b' }}>Duration</div>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: isMobile ? '0.95rem' : undefined }}>{test.duration || 0} min</div>
                  </div>
                  <div>
                    <div style={{ fontSize: isMobile ? '0.8rem' : '0.875rem', color: '#64748b' }}>Questions</div>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: isMobile ? '0.95rem' : undefined }}>{test.questionCount || 0}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: isMobile ? '0.8rem' : '0.875rem', color: '#64748b' }}>Status</div>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: isMobile ? '0.95rem' : undefined }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: booking.status === 'COMPLETED' ? '#dcfce7' :
                                      booking.status === 'CONFIRMED' ? '#dbeafe' :
                                      booking.status === 'PENDING' ? '#fef3c7' :
                                      booking.status === 'CANCELLED' ? '#fee2e2' : '#f3f4f6',
                        color: booking.status === 'COMPLETED' ? '#166534' :
                             booking.status === 'CONFIRMED' ? '#1e40af' :
                             booking.status === 'PENDING' ? '#92400e' :
                             booking.status === 'CANCELLED' ? '#991b1b' : '#374151',
                        border: `1px solid ${
                          booking.status === 'COMPLETED' ? '#bbf7d0' :
                          booking.status === 'CONFIRMED' ? '#bfdbfe' :
                          booking.status === 'PENDING' ? '#fde68a' :
                          booking.status === 'CANCELLED' ? '#fecaca' : '#d1d5db'
                        }`
                      }}>
                        {booking.status === 'COMPLETED' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiCheckCircle size={12} />
                            COMPLETED
                          </span>
                        ) : booking.status === 'CONFIRMED' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiCheckCircle size={12} />
                            CONFIRMED
                          </span>
                        ) : booking.status === 'PENDING' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiClock size={12} />
                            PENDING
                          </span>
                        ) : booking.status === 'CANCELLED' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiXCircle size={12} />
                            CANCELLED
                          </span>
                        ) : booking.status}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Approval</div>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: '500',
                        backgroundColor: booking.approvedBy ? '#dcfce7' : '#fef3c7',
                        color: booking.approvedBy ? '#166534' : '#92400e',
                        border: `1px solid ${booking.approvedBy ? '#bbf7d0' : '#fde68a'}`
                      }}>
                        {booking.approvedBy ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiCheckCircle size={12} />
                            APPROVED
                          </span>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <FiClock size={12} />
                            PENDING APPROVAL
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Scheduled</div>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>
                      {booking.scheduledAt ? new Date(booking.scheduledAt).toLocaleDateString() : 'Not scheduled'}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>Price</div>
                    <div style={{ fontWeight: '600', color: '#1e293b' }}>
                      {test.currency} {test.price}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: isMobile ? '0.75rem' : '1rem', flexDirection: 'column' }}>
                  {/* Scheduling Status Badge */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    marginBottom: isMobile ? '4px' : '8px' 
                  }}>
                    {(() => {
                      if (!booking.scheduledAt) {
                        return (
                          <span style={{
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            border: '1px solid #d1d5db'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiClock size={12} />
                              Not Scheduled
                            </span>
                          </span>
                        );
                      }
                      
                      const now = new Date();
                      const scheduledTime = new Date(booking.scheduledAt);
                      const timeDiff = scheduledTime.getTime() - now.getTime();
                      const minutesDiff = timeDiff / (1000 * 60);
                      
                      if (minutesDiff > 5) {
                        return (
                          <span style={{
                            backgroundColor: '#fef3c7',
                            color: '#d97706',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            border: '1px solid #fbbf24'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiClock size={12} />
                              Scheduled for {scheduledTime.toLocaleString()}
                            </span>
                          </span>
                        );
                      } else if (minutesDiff < -120) {
                        return (
                          <span style={{
                            backgroundColor: '#fee2e2',
                            color: '#dc2626',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            border: '1px solid #f87171'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiXCircle size={12} />
                              Expired
                            </span>
                          </span>
                        );
                      } else {
                        return (
                          <span style={{
                            backgroundColor: '#d1fae5',
                            color: '#059669',
                            padding: '4px 12px',
                            borderRadius: '20px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            border: '1px solid #34d399'
                          }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <FiCheckCircle size={12} />
                              Ready to Start
                            </span>
                          </span>
                        );
                      }
                    })()}
                  </div>
                  
                  <div style={{ display: 'flex', gap: isMobile ? '0.5rem' : '1rem', flexDirection: 'row' }}>
                    <button 
                      className="btn btn-primary"
                      onClick={() => {
                        // console.log('Button clicked for test:', test.title);
                        // console.log('Starting exam for booking:', booking.id);
                        // console.log('Exam ID:', test.id);
                        
                        // Check if booking is approved
                        if (!booking.approvedBy) {
                          toast.error('Your booking has not been approved by admin yet. Please wait for admin approval before starting the exam.');
                          return;
                        }
                        
                        // Commented out timing restrictions for testing
                        // // Check if exam is scheduled and validate timing
                        // if (!booking.scheduledAt) {
                        //   toast.error('This exam is not scheduled yet. Please wait for scheduling.');
                        //   return;
                        // }
                        
                        // const now = new Date();
                        // const scheduledTime = new Date(booking.scheduledAt);
                        // const timeDiff = scheduledTime.getTime() - now.getTime();
                        // const minutesDiff = timeDiff / (1000 * 60);
                        
                        // // Only allow starting 5 minutes before scheduled time
                        // if (minutesDiff > 5) {
                        //   const remainingTime = Math.ceil(minutesDiff);
                        //   if (remainingTime > 60) {
                        //     const hours = Math.floor(remainingTime / 60);
                        //     const minutes = remainingTime % 60;
                        //     toast.error(`This exam is scheduled for ${scheduledTime.toLocaleString()}. Please wait ${hours}h ${minutes}m until the scheduled time.`);
                        //   } else {
                        //     toast.error(`This exam is scheduled for ${scheduledTime.toLocaleString()}. Please wait ${remainingTime} minutes until the scheduled time.`);
                        //   }
                        //   return;
                        // }
                        
                        // // Check if exam is too old (more than 2 hours after scheduled time)
                        // if (minutesDiff < -120) {
                        //   toast.error('This exam was scheduled more than 2 hours ago. Please contact your administrator.');
                        //   return;
                        // }
                        
                        // Skip pre-exam checklist modal and start exam directly
                        // setPreExamSelectedTest(test);
                        // setPreExamSelectedBooking(booking);
                        // setPreExamConfirmed(false);
                        // setPreExamModalOpen(true);
                        
                        // Navigate directly to exam without fullscreen
                        navigate(`/exam/${test.id}`);
                      }}
                      style={{
                        flex: 1,
                        padding: isMobile ? '8px 10px' : undefined,
                        fontSize: isMobile ? '0.9rem' : undefined,
                        opacity: booking.approvedBy ? 1 : 0.5,
                        cursor: booking.approvedBy ? 'pointer' : 'not-allowed',
                        // Commented out timing-based styling for testing
                        // opacity: (() => {
                        //   if (!booking.scheduledAt) return 0.5;
                        //   const now = new Date();
                        //   const scheduledTime = new Date(booking.scheduledAt);
                        //   const timeDiff = scheduledTime.getTime() - now.getTime();
                        //   const minutesDiff = timeDiff / (1000 * 60);
                        //   return (minutesDiff > 5 || minutesDiff < -120) ? 0.5 : 1;
                        // })(),
                        // cursor: (() => {
                        //   if (!booking.scheduledAt) return 'not-allowed';
                        //   const now = new Date();
                        //   const scheduledTime = new Date(booking.scheduledAt);
                        //   const timeDiff = scheduledTime.getTime() - now.getTime();
                        //   const minutesDiff = timeDiff / (1000 * 60);
                        //   return (minutesDiff > 5 || minutesDiff < -120) ? 'not-allowed' : 'pointer';
                        // })()
                      }}
                    >
                      {booking.approvedBy ? 'Start Test' : 'Waiting for Approval'}
                    </button>
                    <button 
                      className="btn btn-secondary"
                      onClick={() => {
                        // Generate bill for this booking
                        const bill = {
                          billNumber: `BILL-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                          billDate: new Date(),
                          dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
                          booking: {
                            id: booking.id,
                            scheduledAt: booking.scheduledAt,
                            status: booking.status
                          },
                          exam: {
                            id: test.id,
                            title: test.title,
                            description: test.description,
                            category: test.examCategory?.name || 'Unknown',
                            duration: test.duration,
                            totalMarks: test.totalMarks
                          },
                          customer: {
                            id: user.id,
                            name: `${user.firstName} ${user.lastName}`,
                            email: user.email
                          },
                          amount: {
                            subtotal: test.price || 0,
                            tax: 0,
                            total: test.price || 0,
                            currency: test.currency || 'USD'
                          },
                          payment: null,
                          status: 'PENDING'
                        };
                        handleShowBill(bill);
                      }}
                      style={{ padding: isMobile ? '8px 10px' : undefined, fontSize: isMobile ? '0.9rem' : undefined, flex: 1 }}
                    >
                      <FiFileText style={{ marginRight: '4px' }} />
                      View Bill
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        )}

        {/* Bill Modal */}
        {showBill && (
          <BillModal
            bill={currentBill}
            onClose={handleCloseBill}
            onPrint={() => {
              toast.success('Bill printed successfully!');
            }}
          />
        )}
      </div>
    </div>

    </>
  );
};

export default AvailableTests; 